import { useEffect, useState } from 'preact/hooks'
import { i18n } from 'i18n/i18n'

import { CurrencyValueForm } from 'models/apps'
import { SpinnerError } from 'models/calculator'
import { Calculation, CurrencyExchangeDetails, ExchangeDirection } from 'models/exchange-transfer-calculate'

import { exchangeTransfer } from 'services/exchange-transfer-calculate'

type ExchangeDataHookConfig = {
  DEFAULT_RESPONSE: Calculation
  DEFAULT_REFRESH_TIME: number
  DEFAULT_EXCHANGE_DATA: CurrencyExchangeDetails
}

interface ExchangeDataHookProps {
  isSettingsLoading: boolean
  direction: ExchangeDirection
  baseCurrency: string
  counterCurrency: string
  value: CurrencyValueForm
  discountRate: number
  supportedCurrenciesPairs: string[]
  setExchangeData: (exchangeData: Calculation) => void
  setSpinnerError: (spinnerError: SpinnerError) => void
  isCurrencyPairSupported: (baseCurrency: string, counterCurrency: string) => boolean
  setIsCurrentCurrencySupported: (isCurrentCurrencySupported: boolean) => void
  isBelowMinimalAmount: boolean
  config: ExchangeDataHookConfig
}

export const useExchangeData = ({
  isSettingsLoading,
  direction,
  baseCurrency,
  counterCurrency,
  value,
  discountRate,
  supportedCurrenciesPairs,
  setExchangeData,
  setSpinnerError,
  isCurrencyPairSupported,
  setIsCurrentCurrencySupported,
  isBelowMinimalAmount,
  config,
}: ExchangeDataHookProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const { DEFAULT_RESPONSE, DEFAULT_REFRESH_TIME, DEFAULT_EXCHANGE_DATA } = config

  useEffect(() => {
    if (isSettingsLoading || supportedCurrenciesPairs.length === 0) {
      setIsLoading(false)
      return
    }

    if (!value.isValueCorrect || isBelowMinimalAmount) {
      setExchangeData(DEFAULT_RESPONSE)
      setIsLoading(false)
      return
    }

    const fetchExchangeData = async () => {
      try {
        if (!isCurrencyPairSupported(baseCurrency, counterCurrency)) {
          setIsCurrentCurrencySupported(false)
          return
        }

        const exchangeDetails: CurrencyExchangeDetails = {
          baseAmount: direction === 'forward' ? value.currentValue : '',
          counterAmount: direction === 'forward' ? '' : value.currentValue,
          baseCurrency: direction === 'forward' ? baseCurrency : counterCurrency,
          counterCurrency: direction === 'forward' ? counterCurrency : baseCurrency,
          direction,
          discountRate,
          headers: DEFAULT_EXCHANGE_DATA.headers,
        }

        const response = await exchangeTransfer(exchangeDetails)
        setExchangeData(response.calculation)
        setSpinnerError({ isErrorVisible: false })
      } catch (e: unknown) {
        const err = (typeof e === 'object' && e !== null ? e : {}) as { status?: number }
        if (err?.status === 429) {
          setSpinnerError({
            isErrorVisible: true,
            message: i18n('calculator.spinnerError.toManyRequests'),
          })
        } else {
          setSpinnerError({ isErrorVisible: true })
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchExchangeData()
    const interval = setInterval(fetchExchangeData, DEFAULT_REFRESH_TIME)
    return () => clearInterval(interval)
  }, [
    isSettingsLoading,
    direction,
    baseCurrency,
    counterCurrency,
    value,
    supportedCurrenciesPairs,
    isBelowMinimalAmount,
    discountRate,
  ])

  return { isExchangeDataLoading: isLoading }
}
